import { ArrowRight, Dumbbell, Heart, ShieldCheck, Sparkles, UsersRound } from "lucide-react";
import { Link } from "wouter";
import { CtaBand } from "@/components/site/CtaBand";
import { PageHero } from "@/components/site/PageHero";
import { SiteLayout } from "@/components/site/SiteLayout";

const possibilities = [
  ["Carry what matters", "Groceries. Luggage. Garden soil. A grandchild who still asks to be picked up."],
  ["Go where life takes you", "Uneven trails, long airport terminals, stadium steps, and afternoons that do not revolve around finding a place to sit."],
  ["Trust your next step", "Confidence grows when strength, balance, and movement skills are practiced—not merely hoped for."],
];

export default function VitalityClass() {
  return (
    <SiteLayout>
      <PageHero
        currentPage="Vitality Program"
        eyebrow="Fitness for adults 50+ in Overland Park"
        title={<>How do you want to <em>age?</em></>}
        description="Not simply how many years you want—but how you want those years to feel. Strong enough for the things you carry. Confident enough for the places you go. Connected to people who keep showing up beside you."
        tone="blue"
        aside={
          <div className="vitality-hero-image">
            <img
              src="/manus-storage/core-vitality-step-up-clinic-nobars_e8f21156.webp"
              alt="An active woman in her 60s performing a controlled step-up exercise with a supportive coach nearby"
              width="1600"
              height="1280"
            />
            <span>Movement for the life ahead</span>
          </div>
        }
      />

      <section className="page-section page-section-white" aria-labelledby="picture-title">
        <div className="container vitality-story-grid">
          <div className="vitality-story-copy">
            <p className="section-kicker">Picture the life you want to keep living</p>
            <h2 id="picture-title">Your future is made of ordinary moments worth protecting.</h2>
            <p>Maybe you want to keep walking the course instead of watching from the cart. Get down on the floor with the people you love. Travel without wondering whether your body can handle the day. Keep tending the garden, joining the pickleball game, or saying yes when plans appear.</p>
            <p>Vitality is built around those moments. It is an encouraging fitness experience for adults 50+ who want to continue developing strength, balance, mobility, and confidence in a supportive setting.</p>
          </div>
          <div className="possibility-stack">
            {possibilities.map(([title, body], index) => (
              <article key={title}>
                <span>0{index + 1}</span>
                <div><h3>{title}</h3><p>{body}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="page-section vitality-benefits" aria-labelledby="benefits-title">
        <div className="container">
          <div className="section-heading centered">
            <p className="section-kicker">Why guided exercise matters after 50</p>
            <h2 id="benefits-title">Build more than fitness. Build options.</h2>
            <p>Exercise cannot stop time, but evidence supports staying active as a meaningful part of maintaining strength, mobility, balance, and day-to-day independence as people age.</p>
          </div>
          <div className="benefit-card-grid">
            <article><Dumbbell /><h3>Strength for real life</h3><p>Resistance exercise can help maintain muscle and support the physical demands of walking, stairs, lifting, and everyday tasks.</p></article>
            <article><ShieldCheck /><h3>Balance and confidence</h3><p>Balance-focused activity gives you opportunities to practice control and adapt to the situations you meet outside the exercise space.</p></article>
            <article><Heart /><h3>Energy for what matters</h3><p>Aerobic activity is one part of a well-rounded routine that can support health, stamina, sleep, and the ability to participate in daily life.</p></article>
            <article><UsersRound /><h3>Encouragement and connection</h3><p>Group exercise creates a place to practice consistently, notice progress, and be encouraged by people working toward their own goals.</p></article>
          </div>
          <div className="evidence-note">
            <Sparkles size={20} aria-hidden="true" />
            <p>These statements reflect general guidance from the <a href="https://www.cdc.gov/physical-activity-basics/guidelines/older-adults.html" target="_blank" rel="noreferrer">Centers for Disease Control and Prevention</a> and the <a href="https://www.nia.nih.gov/news/how-can-strength-training-build-healthier-bodies-we-age" target="_blank" rel="noreferrer">National Institute on Aging</a>. Exercise should be adapted to each person’s abilities and health considerations.</p>
          </div>
        </div>
      </section>

      <section className="page-section vitality-guidance" aria-labelledby="guidance-title">
        <div className="container guidance-grid">
          <div className="guidance-image">
            <img src="/manus-storage/core-guided-movement_592589da.webp" alt="A woman over 60 practicing a guided functional strength movement with a supportive coach" width="1200" height="1600" loading="lazy" />
          </div>
          <div>
            <p className="section-kicker light">Guidance makes the difference</p>
            <h2 id="guidance-title">Meet yourself where you are. Keep moving toward what matters.</h2>
            <p>Vitality is not about comparing your body to someone else’s or proving you can keep up. It is about practicing useful movement, building capacity over time, and having people around you who understand that every starting point is different.</p>
            <p>You will receive class details after you inquire. We intentionally begin with a personal call so the office can learn what you are looking for and answer your questions.</p>
            <Link className="button-light" href="/vitality-inquiry">Ask about Vitality <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      <CtaBand
        eyebrow="Your next chapter can be active"
        title="Choose how you want to keep showing up."
        body="Share your contact information and someone from the CORE office will call you with the details and answer your questions about Vitality."
        primaryLabel="Inquire about Vitality"
        primaryHref="/vitality-inquiry"
      />
    </SiteLayout>
  );
}
